<template>
  <div>
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <!-- Header Section -->
    <div class="row">
      <div class="col">
        <card class="welcome-card">
          <div class="welcome-hero">
            <img
              src="/img/pandas/biku-bot.png"
              alt=""
              class="welcome-panda"
            />
            <div class="welcome-content">
              <h3 class="card-title mb-1">Welcome back, {{ userName }}!</h3>
              <p class="welcome-subtitle">{{ welcomeSubtitle }}</p>

              <div class="welcome-stats">
                <div class="stat">
                  <div class="stat-value">
                    {{ overallProgress.completed_tasks }}<span class="stat-suffix">/{{ overallProgress.total_tasks }}</span>
                  </div>
                  <div class="stat-label">Tasks done</div>
                </div>
                <div class="stat">
                  <div class="stat-value">
                    {{ categoriesComplete }}<span class="stat-suffix">/{{ categories.length || 0 }}</span>
                  </div>
                  <div class="stat-label">Categories complete</div>
                </div>
                <div class="stat">
                  <div class="stat-value">
                    {{ Math.round(overallProgress.completion_percentage) }}<span class="stat-suffix">%</span>
                  </div>
                  <div class="stat-label">Setup complete</div>
                </div>
              </div>

              <div class="welcome-progress-wrapper">
                <div class="progress welcome-progress" style="height: 12px;">
                  <div
                    class="progress-bar bg-primary"
                    role="progressbar"
                    :style="{ width: overallProgress.completion_percentage + '%' }"
                    :aria-valuenow="overallProgress.completion_percentage"
                    aria-valuemin="0"
                    aria-valuemax="100">
                  </div>
                </div>
                <div class="welcome-progress-ticks" aria-hidden="true">
                  <span class="tick" style="left: 25%"></span>
                  <span class="tick" style="left: 50%"></span>
                  <span class="tick" style="left: 75%"></span>
                </div>
              </div>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Categories and Tasks -->
    <div class="row">
      <div class="col">
        <!-- Section header row — bare, no wrapping card -->
        <div class="setup-tasks-header d-flex justify-content-between align-items-center mb-3">
          <h4 class="setup-tasks-title mb-0">Setup tasks</h4>
          <div class="d-flex align-items-center setup-tasks-actions">
            <el-tooltip
              content="Scan your account and auto-mark any tasks whose setup is already done"
              effect="light"
              placement="top"
              :open-delay="300">
              <base-button
                variant="outlined"
                tone="primary"
                size="sm"
                :loading="checkingCompletion"
                @click="runCompletionCheck"
                class="check-completion-btn">
                <i v-if="!checkingCompletion" class="tim-icons icon-refresh-02 mr-1"></i>
                Check completion
              </base-button>
            </el-tooltip>
            <span class="text-white-50 small ml-3 mr-2">Show completed</span>
            <el-switch
              :value="!hideCompleted"
              @input="toggleCompletedCategories"
              active-color="#e14eca"
            />
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="categories.length === 0 && !loading" class="text-center py-5">
          <div class="font-icon-detail">
            <span style="font-size: 3em; color: #e3e3e3">
              <i class="tim-icons icon-spaceship"></i>
            </span>
            <h4 class="mt-3">No tasks available</h4>
            <p class="text-muted">Please check back later or contact your administrator</p>
          </div>
        </div>

        <!-- Categories List -->
        <div v-else class="categories-list">
            <div
              v-for="category in filteredCategories"
              :key="category.id"
              class="category-section mb-3"
            >
              <!-- Category Header -->
              <div
                class="category-header d-flex align-items-center p-3"
                @click="toggleCategory(category.id)"
                :class="{ 'active': expandedCategoryId === category.id }"
              >
                <div class="category-icon mr-3" :style="categoryIconStyle(category)">
                  <i :class="category.icon"></i>
                </div>

                <div class="flex-grow-1">
                  <h5 class="mb-1">{{ category.name }}</h5>
                  <div class="d-flex align-items-center text-white-50 small">
                    <span>
                      {{ category.completed_tasks }} of {{ category.total_tasks }} completed
                    </span>
                    <span class="mx-2">•</span>
                    <span>{{ Math.round(category.completion_percentage) }}%</span>
                  </div>
                </div>

                <div class="category-progress mr-3" style="width: 100px;">
                  <div class="progress category-progress-track" style="height: 4px;">
                    <div
                      class="progress-bar"
                      :class="category.completion_percentage === 100 ? 'bg-success' : 'bg-primary'"
                      :style="{ width: category.completion_percentage + '%' }">
                    </div>
                  </div>
                </div>

                <div class="expand-indicator">
                  <i class="tim-icons icon-minimal-down" :class="{ 'rotated': expandedCategoryId === category.id }"></i>
                </div>
              </div>

              <!-- Tasks List -->
              <transition name="expand">
                <div v-if="expandedCategoryId === category.id" class="tasks-container">
                  <div v-if="!category.tasks || category.tasks.length === 0" class="empty-tasks text-center py-4">
                    <i class="tim-icons icon-check-2 text-white-50"></i>
                    <p class="text-white-50 mt-2">No tasks in this category</p>
                  </div>

                  <div v-else class="tasks-list">
                    <div
                      v-for="task in category.tasks"
                      :key="task.id"
                      class="task-item d-flex align-items-start"
                      :class="getTaskClass(task)"
                    >
                      <!-- Task Status: every task gets a real checkbox the user can toggle.
                           While a toggle is in flight the active row swaps to a spinner and
                           all other rows' checkboxes are disabled, so clicking a second
                           checkbox can't trigger a phantom flip that snaps back when the
                           first request returns. Permission-locked tasks render a lock
                           icon instead so users see them but can't toggle. -->
                      <div class="task-status mr-3">
                        <el-tooltip
                          v-if="task.permission_locked"
                          content="Your role doesn't have access to this task"
                          effect="light"
                          placement="top"
                          :open-delay="300">
                          <span class="checkbox-locked">
                            <i class="tim-icons icon-lock-circle"></i>
                          </span>
                        </el-tooltip>
                        <span v-else-if="completingTaskId === task.id" class="checkbox-spinner">
                          <i class="fas fa-spinner fa-spin"></i>
                        </span>
                        <label v-else class="custom-checkbox mb-0" :class="{ 'is-disabled': !!completingTaskId }">
                          <input
                            type="checkbox"
                            :checked="task.is_completed"
                            :disabled="!!completingTaskId"
                            @change="toggleTask(task.id)"
                          />
                          <span class="checkbox-mark">
                            <i class="tim-icons icon-check-2"></i>
                          </span>
                        </label>
                      </div>

                      <!-- Task Content -->
                      <div class="task-content flex-grow-1">
                        <div class="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 class="task-name mb-1">{{ task.name }}</h6>
                            <p class="task-description text-white-50 small mb-0">{{ task.description }}</p>
                          </div>

                          <div class="task-actions d-flex align-items-center">
                            <badge
                              v-if="task.is_completed && task.completed_at"
                              type="success"
                              class="mr-2"
                              :title="task.completed_by_name ? 'Marked complete by ' + task.completed_by_name : ''">
                              {{ formatCompletionMeta(task) }}
                            </badge>
                            <div class="task-actions-buttons">
                              <base-button
                                v-if="task.docs_url"
                                @click.stop="openDocs(task.docs_url)"
                                link
                                type="primary"
                                size="sm">
                                <i class="tim-icons icon-link-72 mr-1"></i> Help article
                              </base-button>
                              <base-button
                                v-if="task.video_url && task.status === 'available' && !task.is_completed"
                                @click.stop="openVideo(task.video_url)"
                                type="link"
                                size="sm"
                                title="Watch tutorial">
                                <i class="tim-icons icon-video-66"></i>
                              </base-button>
                              <base-button
                                v-if="!task.permission_locked && task.status === 'available' && !task.is_completed"
                                @click.stop="navigateToTask(task)"
                                :loading="navigatingTaskId === task.id"
                                variant="outlined"
                                tone="primary"
                                size="sm">
                                <template #loading>
                                  <i v-if="navigatingTaskId === task.id" class="fas fa-spinner fa-spin mr-2"></i>
                                </template>
                                Start <i v-if="navigatingTaskId !== task.id" class="tim-icons icon-minimal-right ml-1"></i>
                              </base-button>
                              <base-button
                                v-else-if="!task.permission_locked && task.is_completed && task.feature_url"
                                @click.stop="navigateToTask(task)"
                                :loading="navigatingTaskId === task.id"
                                variant="outlined"
                                tone="success"
                                size="sm">
                                <template #loading>
                                  <i v-if="navigatingTaskId === task.id" class="fas fa-spinner fa-spin mr-2"></i>
                                </template>
                                Review <i v-if="navigatingTaskId !== task.id" class="tim-icons icon-minimal-right ml-1"></i>
                              </base-button>
                            </div>

                            <span
                              v-if="task.permission_locked"
                              class="badge badge-secondary badge-sm ml-2"
                              title="Your role doesn't have access to this task">
                              No access
                            </span>
                            <span
                              v-else-if="task.status === 'locked'"
                              class="badge badge-info badge-sm ml-2">
                              Locked
                            </span>
                            <span
                              v-else-if="task.status === 'blocked'"
                              class="badge badge-warning badge-sm ml-2">
                              Blocked
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
        </div>
      </div>
    </div>

    <!-- AI onboarding assistant -->
    <chat-assistant mode="fab" />
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { Badge } from '@/components';
import ChatAssistant from './ChatAssistant.vue';
import { markPermissionLocks, computeOverallProgress } from '@/util/wizardPermissions';

export default {
  name: 'OnboardingWizard',
  components: {
    Badge,
    ChatAssistant
  },
  data() {
    return {
      loading: false,
      companyId: localStorage.getItem('companyId'),
      userName: (() => {
        try {
          const name = JSON.parse(localStorage.getItem('profile'))?.first_name || 'there';
          return name.charAt(0).toUpperCase() + name.slice(1);
        } catch { return 'there'; }
      })(),

      categories: [],
      expandedCategoryId: null,
      hideCompleted: false,

      overallProgress: {
        total_tasks: 0,
        completed_tasks: 0,
        completion_percentage: 0
      },

      completingTaskId: null,
      navigatingTaskId: null,
      checkingCompletion: false
    };
  },

  computed: {
    filteredCategories() {
      if (!this.hideCompleted) {
        return this.categories;
      }
      return this.categories.filter(c => c.completion_percentage < 100);
    },

    /** Number of categories that are 100% complete. */
    categoriesComplete() {
      return this.categories.filter(c => c.completion_percentage === 100).length;
    },

    /**
     * Dynamic one-liner under the welcome heading. Adapts to progress so the page
     * feels alive instead of repeating the same generic blurb every visit.
     */
    welcomeSubtitle() {
      const pct = this.overallProgress.completion_percentage || 0;
      const done = this.overallProgress.completed_tasks || 0;
      if (done === 0) return 'Pick any task below to get started.';
      if (pct === 100) return 'All tasks complete. You\'re fully set up. 🎉';
      if (pct >= 90) return 'Almost launch-ready, just a few left.';
      if (pct >= 75) return 'Most of the way there. Strong finish ahead.';
      if (pct >= 50) return 'You\'re past the halfway mark. Keep going.';
      if (pct >= 25) return 'Nice momentum. Keep checking tasks off.';
      return 'Great start. Keep the streak going.';
    }
  },

  methods: {
    async getAuthToken() {
      try {
        const session = await Auth.currentSession();
        return session.idToken.jwtToken;
      } catch (err) {
        console.error(err);
        return null;
      }
    },

    async getOnboardingProgress(showLoader = true, autoCheck = false) {
      if (showLoader) this.loading = true;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/onboarding${autoCheck ? '?autoCheck=true' : ''}`;

        const response = await axios.get(url, config);

        // Mark tasks the current user can't reach as permission_locked so the
        // template renders them as non-interactive. Permission-locked tasks stay
        // counted in the denominator for consistent reporting across roles, but
        // beta-hidden tasks (e.g. Point of Sale for non-beta companies) are
        // dropped entirely, so recompute overall progress from the visible
        // categories rather than trusting the API total.
        this.categories = markPermissionLocks(response.data.categories || []);
        this.overallProgress = computeOverallProgress(this.categories);

        // Auto-expand first incomplete category on initial load
        if (!this.expandedCategoryId && this.categories.length > 0) {
          const firstIncomplete = this.categories.find(c => c.completion_percentage < 100);
          if (firstIncomplete) {
            this.expandedCategoryId = firstIncomplete.id;
          } else if (this.categories.length > 0) {
            this.expandedCategoryId = this.categories[0].id;
          }
        }

      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.$notify({
            type: 'danger',
            title: 'Error',
            message: 'Failed to load onboarding progress.',
            duration: 3000
          });
        }
      } finally {
        this.loading = false;
      }
    },

    async runCompletionCheck() {
      if (this.checkingCompletion) return;
      this.checkingCompletion = true;

      const beforeCompleted = this.overallProgress.completed_tasks;

      try {
        await this.getOnboardingProgress(false, true);
        const afterCompleted = this.overallProgress.completed_tasks;
        const newlyCompleted = afterCompleted - beforeCompleted;

        this.$notify({
          type: newlyCompleted > 0 ? 'success' : 'info',
          title: 'Check complete',
          message: newlyCompleted > 0
            ? `${newlyCompleted} task${newlyCompleted === 1 ? '' : 's'} marked complete based on your setup.`
            : 'No new tasks were detected as complete.',
          duration: 3000
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.checkingCompletion = false;
      }
    },

    toggleCategory(categoryId) {
      if (this.expandedCategoryId === categoryId) {
        this.expandedCategoryId = null;
      } else {
        this.expandedCategoryId = categoryId;
      }
    },

    toggleCompletedCategories() {
      this.hideCompleted = !this.hideCompleted;
    },

    getTaskClass(task) {
      return {
        'task-completed': task.is_completed,
        'task-locked': task.status === 'locked',
        'task-blocked': task.status === 'blocked',
        'task-available': task.status === 'available' && !task.is_completed,
        'task-permission-locked': task.permission_locked
      };
    },

    async navigateToTask(task) {
      if (this.navigatingTaskId) return;
      this.navigatingTaskId = task.id;

      try {
        await this._resolveAndNavigate(task);
      } finally {
        this.navigatingTaskId = null;
      }
    },

    async _resolveAndNavigate(task) {
      // Parse path and query params from feature_url (e.g. "/purchases/settings?tab=4")
      const [path, queryString] = task.feature_url.split('?');
      const query = {
        onboarding: 'true',
        categoryId: this.expandedCategoryId,
        taskId: task.id
      };

      if (queryString) {
        new URLSearchParams(queryString).forEach((v, k) => { query[k] = v; });
      }

      // Resolve group-specific IDs
      const group = JSON.parse(localStorage.getItem('group'));
      if (group && path === '/purchases/settings') {
        query.id = group.payment_settings_id;
      }
      if (group && path === '/photos/settings') {
        query.id = group.photo_settings_id;
      }
      if (group && path === '/mail/automated/settings') {
        query.id = group.automated_email_settings_id;
      }
      if (group && path === '/settings/group') {
        query.id = group.id;
      }

      // For pages that need a game in localStorage (analytics, leaderboard settings, communication screen)
      const gameLocalStoragePages = ['/games/clues/configure/display', '/dashboard/game/leaderboard/settings', '/dashboard/game/leaderboard', '/dashboard/game/analytics'];
      if (gameLocalStoragePages.includes(path) && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/games`;
          const response = await axios.get(url, config);
          if (response.data && response.data.length > 0) {
            localStorage.setItem('game', JSON.stringify({ id: response.data[0].id }));
            localStorage.setItem('selectedGameStats', JSON.stringify(response.data[0]));
          }
        } catch (err) {
          console.error('Failed to fetch games:', err);
        }
      }

      // For project board tasks, auto-redirect to first project
      if ((path === '/projects/board' || path === '/projects/ticket') && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/projects?count=1&offset=0`;
          const response = await axios.get(url, config);
          const projects = response.data.projects || response.data;
          if (projects && projects.length > 0) {
            const projectId = projects[0].id;
            // For ticket detail, also fetch first ticket
            if (path === '/projects/ticket') {
              try {
                const ticketsUrl = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/projects/${projectId}/tickets?count=1&offset=0&status=&priority=&type=&assignee=&reporter=&key=&summary=`;
                const ticketsResponse = await axios.get(ticketsUrl, config);
                const tickets = ticketsResponse.data.tickets || ticketsResponse.data;
                if (tickets && tickets.length > 0) {
                  this.$router.push({ path: `/projects/${projectId}/tickets/${tickets[0].id}`, query });
                  return;
                }
              } catch (e) {
                console.error('Failed to fetch tickets:', e);
              }
            }
            this.$router.push({ path: `/projects/${projectId}`, query });
            return;
          }
        } catch (err) {
          console.error('Failed to fetch projects:', err);
        }
        this.$router.push({ path: '/projects', query: { count: 10, page: 1, ...query } });
        return;
      }

      // For waiver settings, auto-redirect to first survey
      if (path === '/waivers/settings' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/waiver-settings`;
          const response = await axios.get(url, config);
          const types = response.data.waiver_setting_types;
          if (types && types.length > 0) {
            const traditional = types.find(t => t.type === 'traditional');
            query.id = traditional ? traditional.id : types[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch waiver settings:', err);
        }
        this.$router.push({ path: '/waivers/settings', query });
        return;
      }

      // For customer detail tasks, auto-redirect to first customer
      if (path === '/customers/details' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/customers?count=1&offset=0&first_name=&last_name=&email=&phone=&game=&tag=`;
          const response = await axios.get(url, config);
          const customers = response.data.customers;
          if (customers && customers.length > 0) {
            query.id = customers[0].end_user_id;
          }
        } catch (err) {
          console.error('Failed to fetch customers:', err);
        }
        this.$router.push({ path: '/customers/details', query });
        return;
      }

      // For survey edit tasks, auto-redirect to first survey
      if (path === '/surveys/edit' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/surveys`;
          const response = await axios.get(url, config);
          const surveys = response.data.surveys;
          if (surveys && surveys.length > 0) {
            query.id = surveys[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch surveys:', err);
        }
        this.$router.push({ path: '/surveys/edit', query });
        return;
      }

      // For survey results task, auto-redirect to first survey results
      if (path === '/surveys/results' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/surveys`;
          const response = await axios.get(url, config);
          const surveys = response.data.surveys;
          if (surveys && surveys.length > 0) {
            query.id = surveys[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch surveys:', err);
        }
        this.$router.push({ path: '/surveys/results', query });
        return;
      }

      // For transaction detail task, auto-redirect to first completed transaction
      if (path === '/purchases/transactions/details' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/transactions?count=1&offset=0&first_name=&last_name=&email=&payment=&to=&from=&t_id=&status=1&source=`;
          const response = await axios.get(url, config);
          const transactions = response.data.transactions;
          if (transactions && transactions.length > 0) {
            query.id = transactions[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch transactions:', err);
        }
        this.$router.push({ path: '/purchases/transactions/details', query });
        return;
      }

      // /games/history: only deep-link into a specific instance for the
      // tracker-details task. The history-list task stays on the list page.
      if (path === '/games/history' && group) {
        const wantsInstanceDetail = task.id === 51;
        if (wantsInstanceDetail) {
          try {
            const token = await this.getAuthToken();
            const config = { headers: { Authorization: token } };
            const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/game-instances?count=1&offset=0&game=&game_master=&to=&from=`;
            const response = await axios.get(url, config);
            const instances = response.data.gameInstances || response.data;
            if (instances && instances.length > 0) {
              query.id = instances[0].id;
              this.$router.push({ path: '/games/history/instance', query });
              return;
            }
          } catch (err) {
            console.error('Failed to fetch game instances:', err);
          }
        }
        this.$router.push({ path, query });
        return;
      }

      // For event edit tasks that point to the list, auto-redirect to first game's edit page.
      // The Ratings tab lives on the list page itself, so that task stays on /games/settings.
      if (path === '/games/settings' && query.tab !== 'ratings' && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/games`;
          const response = await axios.get(url, config);
          if (response.data && response.data.length > 0) {
            query.id = response.data[0].id;
          } else {
            query.id = '';
          }
        } catch (err) {
          console.error('Failed to fetch games:', err);
          query.id = '';
        }
        this.$router.push({ path: '/games/edit', query });
        return;
      }

      this.$router.push({ path, query });
    },

    async toggleTask(taskId) {
      if (this.completingTaskId) return;

      // Defense-in-depth: the locked-state template renders a lock icon
      // instead of a checkbox, but in case the toggle is reached another way,
      // bail out so the user can't claim completion on something their role
      // doesn't have access to.
      const task = this.categories
        .flatMap(c => c.tasks || [])
        .find(t => t.id === taskId);
      if (task && task.permission_locked) return;

      this.completingTaskId = taskId;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/onboarding/${taskId}`;

        const response = await axios.post(url, {}, config);
        const isCompleted = response.data.is_completed;

        this.$notify({
          type: 'success',
          title: isCompleted ? 'Task completed!' : 'Task marked incomplete',
          message: isCompleted ? 'Task has been marked as complete.' : 'Task has been marked as incomplete.',
          duration: 3000
        });

        await this.getOnboardingProgress(false);

      } catch (err) {
        console.error(err);
        this.$notify({
          type: 'danger',
          title: 'Error',
          message: 'Could not update task. Please try again.',
          duration: 3000
        });
      } finally {
        this.completingTaskId = null;
      }
    },

    openVideo(url) {
      window.open(url, '_blank');
    },

    openDocs(url) {
      window.open(url, '_blank');
    },

    /**
     * Tint the category icon tile with the category's brand color.
     * `${color}26` appends 15% alpha (hex 26 = 38/255) for the background;
     * the glyph itself uses the full color. Falls back to gray if no color set.
     */
    categoryIconStyle(category) {
      if (!category.color) return {};
      return {
        background: `${category.color}26`,
        color: category.color,
      };
    },

    /**
     * Format completion attribution: "Completed May 25, 2026" (auto-detected)
     * or "Completed May 25, 2026 by Jana Geddis" (manually checked).
     * Returns empty string if not completed or missing timestamp.
     */
    formatCompletionMeta(task) {
      if (!task.is_completed || !task.completed_at) return '';
      const date = new Date(task.completed_at);
      const dateStr = date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      return task.completed_by_name
        ? `Completed ${dateStr} by ${task.completed_by_name}`
        : `Completed ${dateStr}`;
    },
  },

  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const categoryId = urlParams.get('categoryId');
    const completed = urlParams.get('completed');
    const taskId = urlParams.get('taskId');

    if (categoryId) {
      this.expandedCategoryId = parseInt(categoryId);

      if (completed === 'true' && taskId) {
        this.toggleTask(parseInt(taskId));
      }
    }

    this.getOnboardingProgress();
  }
};
</script>

<style scoped>
/* Tighten the welcome card so the panda dominates more.
   !important because the dashboard theme's .card-body wins on cascade otherwise. */
.welcome-card ::v-deep .card-body {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* flex-start so the shorter side doesn't get phantom whitespace above and below from align-items: center.
   Panda is taller than text on desktop, so text top-aligns and any leftover space sits below the text — feels like normal card padding instead of empty void. */
.welcome-hero {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.welcome-content {
  flex: 1;
  min-width: 0;
}

/* Dynamic subtitle under heading — replaces the corporate paragraph */
.welcome-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: 1.25rem;
}

/* Stat strip: three big numbers across, separated by vertical rules */
.welcome-stats {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.welcome-stats .stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-right: 2rem;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.welcome-stats .stat:last-child {
  border-right: 0;
  padding-right: 0;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.1;
  color: #ffffff;
  letter-spacing: -0.02em;
}

.stat-suffix {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.45);
  margin-left: 2px;
}

.stat-label {
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.55);
}

/* Progress bar with milestone tick marks at 25/50/75% */
.welcome-progress-wrapper {
  position: relative;
}

.welcome-progress-ticks {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.welcome-progress-ticks .tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(0, 0, 0, 0.25);
  transform: translateX(-50%);
}

.welcome-panda {
  width: 200px;
  height: 200px;
  object-fit: contain;
  flex-shrink: 0;
  /* Crop the empty transparent margin around the PNG — pulls the panda visually larger inside the same box */
  margin: -20px 0;
}

/* Trim the panda on tablet so it doesn't dominate the welcome card */
@media (max-width: 991px) and (min-width: 576px) {
  .welcome-panda {
    width: 140px;
    height: 140px;
    margin: -14px 0;
  }
}

/* Mobile: stack vertically — paragraph wraps to many lines, so side-by-side leaves the panda floating in a tall text column. As a hero image on top it reads better.
   Also aggressively tighten card padding + typography — the dashboard theme keeps desktop scale on small viewports and the welcome card balloons without overrides. */
@media (max-width: 575px) {
  /* Only set horizontal padding — keep the parent .card-body top/bottom at 0 from above. Setting `padding: 1rem` here would clobber that and re-bloat the card. */
  .welcome-card ::v-deep .card-body {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .welcome-hero {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0;
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;
  }

  /* Crop the panda's transparent bottom halo — modest enough not to overlap the heading text below */
  .welcome-panda {
    width: 140px;
    height: 140px;
    margin: 0 0 -20px;
  }

  /* Trim the oversized heading on mobile — desktop card-title is too big in a stacked layout */
  .welcome-card ::v-deep .card-title {
    font-size: 1.125rem !important;
    line-height: 1.3 !important;
    margin-top: 0 !important;
    margin-bottom: 0.25rem !important;
  }

  .welcome-subtitle {
    font-size: 0.8125rem;
    margin-bottom: 0.75rem;
  }

  /* Stat strip tightens on mobile */
  .welcome-stats {
    gap: 1rem;
    margin-bottom: 0.75rem;
    justify-content: space-between;
    width: 100%;
  }

  .welcome-stats .stat {
    padding-right: 1rem;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .stat-label {
    font-size: 0.625rem;
  }

  .welcome-progress {
    height: 8px !important;
  }
}

/* Progress tracks — transparent white reads well on dark cards; light mode gets a soft cool gray override below. !important because Bootstrap's .progress { background-color: #e9ecef } would otherwise win on cascade. */
.welcome-progress,
.category-progress-track {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Setup tasks section header — replaces the previous wrapping card */
.setup-tasks-header {
  margin-top: 0.5rem;
}

.setup-tasks-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

/* Category Styles */
.categories-list {
  margin-top: 0;
}

.category-section {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.025);
  overflow: hidden;
  transition: border-color 0.2s ease, background 0.2s ease;
  margin-bottom: 0.75rem;
}

.category-section:hover {
  border-color: rgba(255, 255, 255, 0.16);
}

.category-header {
  cursor: pointer;
  background: transparent;
  transition: background 0.2s ease;
}

.category-header:hover {
  background: rgba(255, 255, 255, 0.03);
}

.category-header.active {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/* Category icon tile — dynamic background + color come from categoryIconStyle()
   on the template. The fallback rgba() values below only apply when no category.color is set. */
.category-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
}

.expand-indicator {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

.expand-indicator i {
  transition: transform 0.3s ease;
}

.expand-indicator i.rotated {
  transform: rotate(180deg);
}

/* Tasks Container — no background, no card-in-card.
   Tasks are borderless ruled rows inside the category card. */
.tasks-container {
  background: transparent;
}

.tasks-list {
  /* No padding — task rows go edge-to-edge of the category card so the divider lines span the full width */
}

.task-item {
  background: transparent;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 0.875rem 1rem;
  transition: background 0.15s ease;
}

.task-item:hover {
  background: rgba(255, 255, 255, 0.025);
}

.task-item.task-locked {
  background: rgba(59, 130, 246, 0.04);
}

.task-item.task-locked:hover {
  background: rgba(59, 130, 246, 0.07);
}

.task-item.task-blocked {
  background: rgba(251, 191, 36, 0.04);
}

.task-item.task-blocked:hover {
  background: rgba(251, 191, 36, 0.07);
}

/* Custom Checkbox — visual stays 20px, tap target grows to 44px via padding */
.custom-checkbox {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 12px;
  margin: -12px;
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-mark {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;
  background: transparent;
}

.custom-checkbox input:checked ~ .checkbox-mark {
  background: #00bf9a;
  border-color: #00bf9a;
}

.custom-checkbox:hover .checkbox-mark {
  border-color: rgba(255, 255, 255, 0.7);
}

/* Icon is always in the DOM so its visibility tracks the input's :checked state directly,
   not Vue's data which lags during the toggle API round-trip. Bigger + bolder for contrast. */
.checkbox-mark i {
  font-size: 14px;
  font-weight: 900;
  color: #ffffff;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.custom-checkbox input:checked ~ .checkbox-mark i {
  opacity: 1;
}

.custom-checkbox.is-disabled {
  cursor: not-allowed;
  opacity: 0.45;

  &:hover .checkbox-mark {
    border-color: rgba(255, 255, 255, 0.45);
  }
}

.checkbox-spinner {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #e14eca;
  font-size: 14px;
}

.checkbox-locked {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 16px;
}

.task-item.task-permission-locked {
  opacity: 0.55;
}

.status-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.status-icon.auto-checked {
  cursor: default;
}

.task-name {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.task-description {
  line-height: 1.4;
  margin-bottom: 0;
}

/* Tag-style completion attribution — sits inline with action buttons */
.completion-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  margin-right: 8px;
  background: rgba(0, 191, 154, 0.12);
  border: 1px solid rgba(0, 191, 154, 0.3);
  border-radius: 4px;
  color: rgba(0, 191, 154, 0.95);
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.3;
}

/* Transitions */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Task action buttons — Bootstrap 4 has no `gap-*` utilities, so use a real flex container */
.task-actions-buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .category-progress {
    display: none;
  }

  /* Keep checkbox + title on the same row. Default .task-item is already `d-flex align-items-start` from the template — leave it as row. */

  /* Only the inner row inside .task-content stacks: title/description above, action buttons full-width below */
  .task-content > .d-flex {
    flex-direction: column;
  }

  .task-actions {
    margin-top: 0.75rem;
    width: 100%;
    flex-wrap: wrap;
  }

  /* Help article shrinks to inline link; Start expands to full width as the primary CTA */
  .task-actions-buttons {
    width: 100%;
    justify-content: space-between;
  }

  .task-actions-buttons > button[type="button"]:last-child,
  .task-actions-buttons > .btn-primary {
    flex: 1 1 auto;
  }
}

/* Clear room for the floating chat assistant on mobile so it does not cover the last Start button */
@media (max-width: 768px) {
  .row:last-of-type {
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }
}
</style>

<style>
/* Light mode overrides — kept unscoped so `.white-content` on the layout root cascades in */
.white-content .welcome-card .text-white,
.white-content .welcome-card .text-white-50 {
  color: rgba(0, 0, 0, 0.75) !important;
}

.white-content .welcome-card .opacity-8 {
  color: rgba(0, 0, 0, 0.7) !important;
}

/* Stat strip + subtitle */
.white-content .welcome-subtitle {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .stat-value {
  color: rgba(0, 0, 0, 0.9);
}

.white-content .stat-suffix {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .stat-label {
  color: rgba(0, 0, 0, 0.55);
}

.white-content .welcome-stats .stat {
  border-right-color: rgba(0, 0, 0, 0.08);
}

.white-content .welcome-stats .stat:last-child {
  border-right: 0;
}

/* Progress milestone tick marks pop on light backgrounds */
.white-content .welcome-progress-ticks .tick {
  background: rgba(0, 0, 0, 0.15);
}

.white-content .category-section {
  border-color: rgba(0, 0, 0, 0.1);
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.white-content .category-section:hover {
  border-color: rgba(0, 0, 0, 0.16);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.white-content .category-header:hover {
  background: rgba(0, 0, 0, 0.025);
}

.white-content .category-header.active {
  border-bottom-color: rgba(0, 0, 0, 0.08);
}

.white-content .category-icon {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.7);
}

.white-content .category-header .text-white-50,
.white-content .category-header h5 {
  color: rgba(0, 0, 0, 0.7) !important;
}

.white-content .expand-indicator {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .tasks-container {
  background: transparent;
}

/* Tasks are borderless ruled rows — keep them on the white card background, divide with a hairline */
.white-content .task-item {
  background: transparent;
  border-top-color: rgba(0, 0, 0, 0.07);
}

.white-content .task-item:hover {
  background: rgba(0, 0, 0, 0.02);
}

.white-content .task-item.task-locked {
  background: rgba(59, 130, 246, 0.05);
}

.white-content .task-item.task-locked:hover {
  background: rgba(59, 130, 246, 0.08);
}

.white-content .task-item.task-blocked {
  background: rgba(251, 191, 36, 0.06);
}

.white-content .task-item.task-blocked:hover {
  background: rgba(251, 191, 36, 0.1);
}

.white-content .task-name {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .task-description {
  color: rgba(0, 0, 0, 0.65) !important;
}

.white-content .completion-tag {
  background: rgba(0, 191, 154, 0.1);
  border-color: rgba(0, 150, 120, 0.4);
  color: rgba(0, 130, 100, 1);
}

.white-content .empty-tasks .text-white-50,
.white-content .empty-tasks i {
  color: rgba(0, 0, 0, 0.45) !important;
}

.white-content .checkbox-mark {
  border-color: rgba(0, 0, 0, 0.3);
}

.white-content .custom-checkbox:hover .checkbox-mark {
  border-color: rgba(0, 0, 0, 0.5);
}

/* Card header "Show completed" label */
.white-content .card .card-header .text-white-50 {
  color: rgba(0, 0, 0, 0.6) !important;
}

/* Setup tasks section header */
.white-content .setup-tasks-title {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .setup-tasks-header .text-white-50 {
  color: rgba(0, 0, 0, 0.6) !important;
}

/* Light-mode progress tracks — soft cool gray, not warm. !important because Bootstrap and the dashboard theme both set .progress { background-color } at a comparable specificity. */
.white-content .welcome-progress,
.white-content .category-progress-track {
  background-color: rgba(0, 0, 0, 0.06) !important;
}
</style>
